import { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DemoContext = createContext(null);

const emptyForm = { name: "", company: "", email: "", telephone: "", business_size: "", message: "" };

export function DemoProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("demo");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const openDemo = (t = "demo") => {
    setType(t);
    setForm(emptyForm);
    setDone(false);
    setOpen(true);
  };
  const close = () => setOpen(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, enquiry_type: type });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DemoContext.Provider value={{ openDemo }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="demo-dialog-overlay"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={close} />
            <motion.div
              className="relative z-10 w-full max-w-lg rounded-3xl border border-white/10 bg-card p-8 shadow-2xl"
              initial={{ y: 40, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
            >
              <button
                onClick={close}
                className="absolute right-5 top-5 text-muted-foreground transition-colors hover:text-white"
                data-testid="demo-dialog-close"
                aria-label="Close"
              >
                <X size={22} weight="bold" />
              </button>

              {done ? (
                <div className="py-8 text-center" data-testid="demo-success">
                  <CheckCircle size={64} weight="fill" className="mx-auto text-primary" />
                  <h3 className="mt-5 text-2xl font-semibold">You're all set.</h3>
                  <p className="mt-2 text-muted-foreground">
                    Your request has landed with our team. We'll reach out to arrange your live walkthrough of FlowCore.
                  </p>
                  <button
                    onClick={close}
                    className="mt-6 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
                    data-testid="demo-success-close"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                    {type === "demo" ? "Book a Demo" : "Get in touch"}
                  </span>
                  <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight">
                    See FlowCore in action
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Tell us a little about your business and we'll tailor the walkthrough.
                  </p>

                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="demo-form">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="d-name" className="text-xs text-muted-foreground">Name *</Label>
                        <Input id="d-name" data-testid="demo-name" value={form.name} onChange={update("name")} placeholder="Jane Smith" className="mt-1 bg-background" />
                      </div>
                      <div>
                        <Label htmlFor="d-company" className="text-xs text-muted-foreground">Company</Label>
                        <Input id="d-company" data-testid="demo-company" value={form.company} onChange={update("company")} placeholder="Acme Ltd" className="mt-1 bg-background" />
                      </div>
                      <div>
                        <Label htmlFor="d-email" className="text-xs text-muted-foreground">Email *</Label>
                        <Input id="d-email" type="email" data-testid="demo-email" value={form.email} onChange={update("email")} placeholder="jane@acme.co.uk" className="mt-1 bg-background" />
                      </div>
                      <div>
                        <Label htmlFor="d-phone" className="text-xs text-muted-foreground">Telephone</Label>
                        <Input id="d-phone" data-testid="demo-phone" value={form.telephone} onChange={update("telephone")} placeholder="07000 000000" className="mt-1 bg-background" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Business Size</Label>
                      <Select value={form.business_size} onValueChange={update("business_size")}>
                        <SelectTrigger className="mt-1 bg-background" data-testid="demo-size">
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-5">1–5 engineers</SelectItem>
                          <SelectItem value="6-20">6–20 engineers</SelectItem>
                          <SelectItem value="21-50">21–50 engineers</SelectItem>
                          <SelectItem value="50+">50+ engineers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="d-msg" className="text-xs text-muted-foreground">Message</Label>
                      <Textarea id="d-msg" data-testid="demo-message" value={form.message} onChange={update("message")} placeholder="What would you like to achieve with FlowCore?" className="mt-1 min-h-[90px] bg-background" />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60"
                      data-testid="demo-submit"
                    >
                      {submitting ? "Sending…" : (<><PaperPlaneTilt size={18} weight="bold" /> Submit request</>)}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DemoContext.Provider>
  );
}

export const useDemo = () => useContext(DemoContext);
